import type { FC } from 'react';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import NewInvitationSvg from '../../assets/new_invite_pic.svg';
import { WelcomeSideBar } from '../general/WelcomeSideBar';
import { ConnectionStatus } from '@pflegenavi/service-provider-shared/api-endpoints';
import { AlreadyAcceptedInfo } from '../general/AlreadyAcceptedInfo';

interface NewInvitationContentProps {
  loading: boolean;
  accountAffiliationName?: string;
  connectionStatus?: ConnectionStatus;
  onAcceptInvitation: () => void;
  onRejectInvitation: () => void;
}

export const NewInvitationContent: FC<NewInvitationContentProps> = ({
  loading,
  accountAffiliationName,
  connectionStatus,
  onAcceptInvitation,
  onRejectInvitation,
}) => {
  const { t } = useTranslation();

  if (!accountAffiliationName || !connectionStatus) {
    return (
      <Grid container>
        <Grid item md={3}>
          <WelcomeSideBar
            title={t('service-provider-app.new-invitation.hello')}
            subtitle={t(
              'service-provider-app.new-invitation.new-invitation-arrived'
            )}
          />
        </Grid>
        <Grid item md={9}>
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={{
              width: '100%',
              height: '100vh',
            }}
          >
            <Stack sx={{ maxWidth: 500 }} gap={3}>
              <Stack gap={0.5}>
                <Typography variant="h5" color="primary.main">
                  {t('service-provider-app.new-invitation.invitation-invalid')}
                </Typography>
                <Typography variant="subtitle2" color="grey.500">
                  {t(
                    'service-provider-app.new-invitation.invitation-invalid-description'
                  )}
                </Typography>
              </Stack>
              <Stack alignItems="center">
                <img src={NewInvitationSvg} width={350} />
              </Stack>

              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                onClick={onAcceptInvitation}
              >
                {t('service-provider-app.new-invitation.go-to-home')}
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    );
  } else if (connectionStatus !== ConnectionStatus.InvitePending) {
    return (
      <Grid container>
        <Grid item md={3}>
          <WelcomeSideBar
            title={t('service-provider-app.new-invitation.hello')}
            subtitle={t(
              'service-provider-app.new-invitation.new-invitation-arrived'
            )}
          />
        </Grid>
        <Grid item md={9}>
          <AlreadyAcceptedInfo
            connectionStatus={connectionStatus}
            accountAffiliationName={accountAffiliationName}
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item md={3}>
        <WelcomeSideBar
          title={t('service-provider-app.new-invitation.hello')}
          subtitle={t(
            'service-provider-app.new-invitation.new-invitation-arrived'
          )}
        />
      </Grid>
      <Grid item md={9}>
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            width: '100%',
            height: '100vh',
          }}
        >
          <Stack sx={{ maxWidth: 500 }} gap={3}>
            <Stack gap={0.5}>
              <Typography variant="h5" color="primary.main">
                {t(
                  'service-provider-app.new-invitation.nursing-home-invited-you'
                )}
              </Typography>
              <Typography variant="subtitle2" color="grey.500">
                {t(
                  'service-provider-app.new-invitation.nursing-home-invited-you-description'
                )}
              </Typography>
            </Stack>
            <Stack alignItems="center">
              <img src={NewInvitationSvg} width={350} />
            </Stack>

            <Stack direction="row" justifyContent="center" gap={0.5}>
              <Typography variant="subtitle2" color="grey.500">
                {t('service-provider-app.new-invitation.account-affiliation')}:
              </Typography>
              <Typography
                variant="subtitle2"
                color="primary.main"
                fontWeight={700}
              >
                {accountAffiliationName}
              </Typography>
            </Stack>

            <Stack direction="column" justifyContent="center" gap={1}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                onClick={onAcceptInvitation}
              >
                {t('service-provider-app.new-invitation.connect-account')}
              </Button>
              <Button
                variant="text"
                size="large"
                fullWidth
                disabled={loading}
                onClick={onRejectInvitation}
              >
                {t('service-provider-app.new-invitation.reject-invitation')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  );
};
